import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FormInput from '../../components/elements/form_input';
import Button from '../../components/elements/button';
import { Upload, Back, Trash } from '../../assets/icons';
import '../../assets/css/vendor/product-form.css';
import { vendorsApi } from '../../network/apis/vendors';
import { productsApi } from '../../network/apis/products';

const EditProduct = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [categories, setCategories] = useState([]);
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: '',
        price: '',
        discount_price: '',
        stock_quantity: '',
        sku: '',
        weight: '',
        dimensions: '',
        manufacturer: '',
        is_featured: false,
        is_active: true
    });
    
    const [existingImages, setExistingImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [primaryImageId, setPrimaryImageId] = useState(null);
    const [imagesToDelete, setImagesToDelete] = useState([]);

    const fetchProduct = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await vendorsApi.getProductById(id);
            const product = response.data?.data || response.data || response;
            
            setFormData({
                name: product.name || '',
                description: product.description || '',
                category: product.category?.id?.toString() || product.category_id?.toString() || '',
                price: product.price?.toString() || '',
                discount_price: product.discount_price?.toString() || '',
                stock_quantity: product.stock_quantity?.toString() || '0',
                sku: product.sku || '',
                weight: product.weight?.toString() || '',
                dimensions: product.dimensions || '',
                manufacturer: product.manufacturer || '',
                is_featured: product.is_featured || false,
                is_active: product.is_active !== false
            });
            
            // Handle existing images
            const images = product.images || [];
            setExistingImages(images);
            
            // Find primary image
            const primaryImg = images.find(img => img.is_primary);
            if (primaryImg) {
                setPrimaryImageId(primaryImg.id);
            } else if (images.length > 0) {
                setPrimaryImageId(images[0].id);
            }
        } catch (err) {
            console.error('Failed to fetch product:', err);
            setError('Failed to load product. It may not exist or you may not have permission.');
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await productsApi.getCategories();
            const data = response?.results || response?.data?.results || response?.data || response || [];
            setCategories(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    }, []);

    useEffect(() => {
        fetchProduct();
        fetchCategories();
    }, [fetchProduct, fetchCategories]);

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        const uploadedImages = files.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            name: file.name
        }));
        setNewImages(prev => [...prev, ...uploadedImages]);
    };

    const removeExistingImage = (imageId) => {
        setImagesToDelete(prev => [...prev, imageId]);
        setExistingImages(prev => prev.filter(img => img.id !== imageId));
        
        if (primaryImageId === imageId) {
            const remaining = existingImages.filter(img => img.id !== imageId);
            if (remaining.length > 0) {
                setPrimaryImageId(remaining[0].id);
            } else if (newImages.length > 0) {
                setPrimaryImageId('new-0');
            } else {
                setPrimaryImageId(null);
            }
        }
    };

    const removeNewImage = (index) => {
        setNewImages(prev => prev.filter((_, i) => i !== index));
        
        if (primaryImageId === `new-${index}`) {
            if (existingImages.length > 0) {
                setPrimaryImageId(existingImages[0].id);
            } else {
                const remaining = newImages.filter((_, i) => i !== index);
                setPrimaryImageId(remaining.length > 0 ? 'new-0' : null);
            }
        }
    };

    const setPrimaryImage = (imageId) => {
        setPrimaryImageId(imageId);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        // Validation
        if (!formData.name.trim()) {
            setError('Product name is required');
            return;
        }
        if (!formData.category) {
            setError('Please select a category');
            return;
        }
        if (!formData.price || parseFloat(formData.price) <= 0) {
            setError('Please enter a valid price');
            return;
        }
        if (!formData.sku.trim()) {
            setError('SKU is required');
            return;
        }

        setIsSubmitting(true);

        try {
            // Update product data
            const productData = {
                name: formData.name,
                description: formData.description,
                category: parseInt(formData.category),
                price: parseFloat(formData.price),
                discount_price: formData.discount_price ? parseFloat(formData.discount_price) : null,
                stock_quantity: parseInt(formData.stock_quantity) || 0,
                sku: formData.sku,
                weight: formData.weight ? parseFloat(formData.weight) : null,
                dimensions: formData.dimensions,
                manufacturer: formData.manufacturer,
                is_featured: formData.is_featured,
                is_active: formData.is_active
            };

            await vendorsApi.updateProduct(id, productData);

            // Delete removed images
            for (const imageId of imagesToDelete) {
                try {
                    await vendorsApi.deleteProductImage(id, imageId);
                } catch (imgErr) {
                    console.error('Failed to delete image:', imgErr);
                }
            }

            // Upload new images
            for (let i = 0; i < newImages.length; i++) {
                const imageFormData = new FormData();
                imageFormData.append('image', newImages[i].file);
                imageFormData.append('is_primary', primaryImageId === `new-${i}` ? 'true' : 'false');
                
                await vendorsApi.uploadProductImage(id, imageFormData);
            }

            // Update primary image if changed (and it's an existing image)
            if (primaryImageId && typeof primaryImageId === 'number') {
                // API call to set primary image if needed
                // This depends on your backend implementation
            }

            setSuccess('Product updated successfully!');
            setTimeout(() => navigate('/vendor/products'), 1500);
        } catch (err) {
            console.error('Failed to update product:', err);
            const errorData = err.response?.data;
            let errorMessage = 'Failed to update product. Please try again.';
            
            if (errorData?.errors && typeof errorData.errors === 'object') {
                const fieldErrors = Object.entries(errorData.errors);
                if (fieldErrors.length > 0) {
                    const [field, messages] = fieldErrors[0];
                    errorMessage = `${field}: ${Array.isArray(messages) ? messages[0] : messages}`;
                }
            } else if (errorData?.message) {
                errorMessage = errorData.message;
            } else if (errorData?.detail) {
                errorMessage = errorData.detail;
            }
            
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="product-form-page">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading product...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="product-form-page">
            <div className="page-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <img src={Back} alt="Back" />
                    Back to Products
                </button>
                <h1>Edit Product</h1>
            </div>

            {error && (
                <div className="alert alert-error">
                    <span className="alert-icon">⚠️</span>
                    <span className="alert-message">{error}</span>
                    <button type="button" className="alert-close" onClick={() => setError(null)}>×</button>
                </div>
            )}
            {success && (
                <div className="alert alert-success">
                    <span className="alert-icon">✓</span>
                    <span className="alert-message">{success}</span>
                    <button type="button" className="alert-close" onClick={() => setSuccess(null)}>×</button>
                </div>
            )}

            <form onSubmit={handleSubmit} className="product-form">
                <div className="form-grid">
                    <div className="form-main">
                        <section className="form-section">
                            <h2>Basic Information</h2>
                            
                            <FormInput
                                label="Product Name"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                required
                                showLabel
                                placeholder="Enter product name"
                            />

                            <FormInput
                                label="Description"
                                type="textarea"
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                showLabel
                                placeholder="Describe your product..."
                                rows={6}
                                maxLength={2000}
                            />

                            <div className="input-row">
                                <FormInput
                                    label="Category"
                                    type="select"
                                    value={formData.category}
                                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                                    required
                                    showLabel
                                    placeholder="Select category"
                                >
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </FormInput>

                                <FormInput
                                    label="SKU"
                                    value={formData.sku}
                                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                                    required
                                    showLabel
                                    placeholder="e.g., FF-001"
                                />
                            </div>
                        </section>

                        <section className="form-section">
                            <h2>Pricing & Stock</h2>
                            
                            <div className="input-row">
                                <FormInput
                                    label="Price (₦)"
                                    type="number"
                                    value={formData.price}
                                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                                    required
                                    showLabel
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                />

                                <FormInput
                                    label="Discount Price (₦)"
                                    type="number"
                                    value={formData.discount_price}
                                    onChange={(e) => setFormData({...formData, discount_price: e.target.value})}
                                    showLabel
                                    placeholder="Optional"
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            <FormInput
                                label="Stock Quantity"
                                type="number"
                                value={formData.stock_quantity}
                                onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                                showLabel
                                placeholder="0"
                                min="0"
                            />
                        </section>

                        <section className="form-section">
                            <h2>Product Details</h2>
                            
                            <div className="input-row">
                                <FormInput
                                    label="Weight (kg)"
                                    type="number"
                                    value={formData.weight}
                                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                                    showLabel
                                    placeholder="Optional"
                                    min="0"
                                    step="0.01"
                                />

                                <FormInput
                                    label="Dimensions"
                                    value={formData.dimensions}
                                    onChange={(e) => setFormData({...formData, dimensions: e.target.value})}
                                    showLabel
                                    placeholder="e.g., 10x20x5 cm"
                                />
                            </div>

                            <FormInput
                                label="Manufacturer"
                                value={formData.manufacturer}
                                onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                                showLabel
                                placeholder="Brand or manufacturer name"
                            />
                        </section>
                    </div>

                    <div className="form-sidebar">
                        <section className="form-section">
                            <h2>Product Images</h2>
                            
                            <div className="image-upload-area">
                                {(existingImages.length > 0 || newImages.length > 0) ? (
                                    <div className="image-previews">
                                        {/* Existing images */}
                                        {existingImages.map((img) => (
                                            <div 
                                                key={img.id} 
                                                className={`image-preview ${primaryImageId === img.id ? 'primary' : ''}`}
                                                onClick={() => setPrimaryImage(img.id)}
                                            >
                                                <img src={img.image || img.url} alt="Product" />
                                                <button 
                                                    type="button" 
                                                    className="remove-image"
                                                    onClick={(e) => { e.stopPropagation(); removeExistingImage(img.id); }}
                                                >
                                                    ×
                                                </button>
                                                {primaryImageId === img.id && (
                                                    <span className="primary-badge">Primary</span>
                                                )}
                                            </div>
                                        ))}
                                        
                                        {/* New images */}
                                        {newImages.map((img, index) => (
                                            <div 
                                                key={`new-${index}`}
                                                className={`image-preview ${primaryImageId === `new-${index}` ? 'primary' : ''}`}
                                                onClick={() => setPrimaryImage(`new-${index}`)}
                                            >
                                                <img src={img.preview} alt={img.name} />
                                                <button 
                                                    type="button" 
                                                    className="remove-image"
                                                    onClick={(e) => { e.stopPropagation(); removeNewImage(index); }}
                                                >
                                                    ×
                                                </button>
                                                {primaryImageId === `new-${index}` && (
                                                    <span className="primary-badge">Primary</span>
                                                )}
                                                <span className="new-badge">New</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="upload-placeholder">
                                        <img src={Upload} alt="Upload" />
                                        <p>Drop images here or click to upload</p>
                                    </div>
                                )}
                                
                                <div className="file-input-wrapper">
                                    <Button
                                        text={existingImages.length > 0 || newImages.length > 0 ? 'Add More Images' : 'Upload Images'}
                                        variant="outline"
                                        icon={<img src={Upload} alt="Upload" />}
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageUpload}
                                        className="file-input"
                                    />
                                </div>
                                <small>Click an image to set as primary. PNG, JPG up to 5MB each.</small>
                            </div>
                        </section>

                        <section className="form-section">
                            <h2>Product Status</h2>
                            
                            <label className="checkbox-option">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                />
                                <span className="checkbox-label">Active (visible in store)</span>
                            </label>

                            <label className="checkbox-option">
                                <input
                                    type="checkbox"
                                    checked={formData.is_featured}
                                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                                />
                                <span className="checkbox-label">Featured product</span>
                            </label>
                        </section>

                        <section className="form-section danger-zone">
                            <h2>Danger Zone</h2>
                            <p className="danger-text">Deleting a product cannot be undone. All associated data will be permanently removed.</p>
                            <Button
                                text="Delete Product"
                                variant="danger"
                                icon={<img src={Trash} alt="Delete" />}
                                onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
                                        vendorsApi.deleteProduct(id)
                                            .then(() => {
                                                setSuccess('Product deleted successfully');
                                                setTimeout(() => navigate('/vendor/products'), 1000);
                                            })
                                            .catch(_err => {
                                                setError('Failed to delete product');
                                            });
                                    }
                                }}
                            />
                        </section>

                        <div className="form-actions">
                            <Button
                                text="Cancel"
                                variant="outline"
                                onClick={() => navigate(-1)}
                            />
                            <Button
                                text={isSubmitting ? 'Saving...' : 'Save Changes'}
                                type="submit"
                                variant="primary"
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default EditProduct;
